/**
 * Decision 0138 (lessons 1–4 §3, "grants that stick"): a chat grant becomes a policy card. When a person delegates in
 * chat ("you may merge to develop", "don't make me review PRs"), the project lead files one `propose_policy` card in that
 * turn whose Apply option carries `RevisePolicy` (the policy's next document). A project admin applies it (it is a
 * person-only effect); with the person's quote the 0093 relay applies it at once as their decision. Pure.
 */
import { documentViolations } from "./policy"
import { leadOf, policyOf } from "./state"
import type { Capability, OptionEffect, PolicyDocument, PolicyRule, ProjectState } from "./types"

export type RevisePolicyEffect = Extract<OptionEffect, { _tag: "RevisePolicy" }>

/** One rule in words: `start_item (top, own_area)`. */
export const policyRuleText = (r: PolicyRule): string => {
  const w = r.when
  if (!w) return r.capability
  const parts = Object.entries(w)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => (typeof v === "boolean" ? (v ? k : `not ${k}`) : `${k} ${String(v)}`))
  return parts.length > 0 ? `${r.capability} (${parts.join(", ")})` : r.capability
}

const ruleKey = (r: PolicyRule): string => JSON.stringify([r.capability, r.when ?? null])

/** What a revision changes, one line per rule: `+ allow merge…`, `− propose start_item`, `+ never …`. */
export const policyDiffLines = (before: PolicyDocument, after: PolicyDocument): ReadonlyArray<string> => {
  const lines: string[] = []
  for (const verdict of ["allow", "propose"] as const) {
    const was = new Set(before[verdict].map(ruleKey))
    const now = new Set(after[verdict].map(ruleKey))
    for (const r of after[verdict]) if (!was.has(ruleKey(r))) lines.push(`+ ${verdict} ${policyRuleText(r)}`)
    for (const r of before[verdict]) if (!now.has(ruleKey(r))) lines.push(`− ${verdict} ${policyRuleText(r)}`)
  }
  const was = new Set<Capability>(before.never)
  const now = new Set<Capability>(after.never)
  for (const c of after.never) if (!was.has(c)) lines.push(`+ never ${c}`)
  for (const c of before.never) if (!now.has(c)) lines.push(`− never ${c}`)
  return lines
}

/** Why the project lead may not open this policy card now, or null. */
export const policyProposalProblem = (
  s: ProjectState,
  agentId: string,
  e: RevisePolicyEffect,
): string | null => {
  if (leadOf(s)?.id !== agentId)
    return "policy changes are proposed by the project lead: ask the lead (an area lead's or role agent's suggestion goes through the lead)"
  const p = policyOf(s, e.policyId)
  if (!p) return `policy ${e.policyId} is not a policy of this project`
  if (p.revision !== e.revision)
    return `the policy changed since (revision ${p.revision}, not ${e.revision}): propose again from the current one`
  const forced = documentViolations(e.document)
  if (forced.length > 0) return `${forced.join(", ")} stay never for agents`
  if (policyDiffLines(p.document, e.document).length === 0) return "a policy card changes at least one rule"
  return null
}

/** The option of a policy card that carries `RevisePolicy` (the Apply), or null for any other card. */
export const policyEffectOf = (body: {
  readonly _tag: string
  readonly options?: ReadonlyArray<{ readonly key: string; readonly effects: ReadonlyArray<OptionEffect> }>
}): { readonly key: string; readonly effect: RevisePolicyEffect } | null => {
  if (body._tag !== "question") return null
  for (const o of body.options ?? [])
    for (const e of o.effects) if (e._tag === "RevisePolicy") return { key: o.key, effect: e }
  return null
}

/** Whose policy a card changes, in words: "the project's policy", "@lea's policy", "the Growth area's policy". */
export const policySubjectText = (s: ProjectState, policyId: string): string => {
  const p = policyOf(s, policyId as never)
  if (!p) return "a policy"
  switch (p.subject._tag) {
    case "project":
      return "the project's policy"
    case "area": {
      const id = p.subject.areaId
      return `the ${s.areas.find((a) => a.id === id)?.name ?? "area"} area's policy`
    }
    case "agent": {
      const id = p.subject.agentId
      return `@${s.agents.find((a) => a.id === id)?.handle ?? "agent"}'s policy`
    }
  }
}

/** The stale check at apply: the policy moved on since the card opened. */
export const policyStaleProblem = (s: ProjectState, e: RevisePolicyEffect): string | null => {
  const p = policyOf(s, e.policyId)
  if (!p) return `policy ${e.policyId} no longer exists`
  return p.revision === e.revision
    ? null
    : `the policy changed since this card opened (revision ${p.revision}, not ${e.revision}): propose again`
}
